import React, { useState, useEffect, useRef } from 'react';
import { AccountFinal } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/use-toast';
import { 
  ArrowLeft, 
  Mail, 
  Linkedin, 
  Phone, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  User as UserIcon,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl, normalizePlaybookKey } from '@/utils';
import { OUTBOX_POST_GENERATION_NAV_STATE } from '@/lib/outboxPostGenerationNav';
import { resolveEffectiveTenant, isEvasysFamilyTenant } from '@/lib/tenantUtils';
import {
  isEvasysAccountEligibleForExamIntegrityPlaybook,
  isEvasysAccountEligibleForSysGovernancePlaybook,
  getCayuseDefaultSelectedProductContexts,
  getCayuseTargetProductListForContext,
  getEvasysDefaultSelectedProductContexts,
  getEvasysTargetProductListForContext,
  isEvasysNorthAmericaCountry,
  isSysExamProductContextTenant,
} from '@/lib/accountTargetProducts';
import {
  clearPreviewNoExecutionEligible,
  setPreviewNoExecutionEligible,
} from '@/lib/previewNoExecEligibleSession';
import {
  aggregateParsedList,
  describeSequencesFailed,
  fetchPlaybookStatusBodiesAllSettled,
  formatGeneratingProgressLine,
  normalizeRunSequenceIds,
  parsePlaybookStatusData,
  sequencePollPhase,
} from '@/lib/playbookStatusAggregate';
import LinkedInSubtypeBadge from '@/components/common/LinkedInSubtypeBadge';
import { isLinkedInOutboxChannel } from '@/lib/outboxChannel';

const API_BASE_URL = (() => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'https://api.neogtm.com';
  }
  return 'https://api.neogtm.com';
})();

const CHANNEL_ICONS = {
  email: Mail,
  linkedin: Linkedin,
  call: Phone
};

const CREATE_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
const INITIAL_POLL_DELAY_MS = 2000;
const FAST_POLL_INTERVAL_MS = 5000;
const SLOW_POLL_INTERVAL_MS = 10000;
const FAST_POLL_WINDOW_MS = 2 * 60 * 1000;
const MIN_WATCH_WINDOW_MS = 15 * 60 * 1000;
const MAX_WATCH_WINDOW_MS = 60 * 60 * 1000;
const WATCH_WINDOW_PER_MESSAGE_MS = 20 * 1000;

/** Substring from backend when channel eligibility cannot be computed for preview (often HTTP 500). */
const CHANNEL_ELIGIBILITY_PREVIEW_ERROR_SNIPPET =
  'Unable to compute channel eligibility for this playbook preview';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const getPollIntervalMs = (elapsedMs) => (elapsedMs < FAST_POLL_WINDOW_MS ? FAST_POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS);
const computeWatchWindowMs = (estimatedMessages) => {
  const safeMessages = Number.isFinite(estimatedMessages) && estimatedMessages > 0 ? estimatedMessages : 0;
  return clamp(safeMessages * WATCH_WINDOW_PER_MESSAGE_MS, MIN_WATCH_WINDOW_MS, MAX_WATCH_WINDOW_MS);
};

// Tier to touch count mapping - handles both "1" and "T1" formats
const TIER_TOUCH_COUNT = {
  '1': 6,
  'T1': 6,
  'Tier 1': 6,
  '2': 5,
  'T2': 5,
  'Tier 2': 5,
  '3': 4,
  'T3': 4,
  'Tier 3': 4,
  '4': 3,
  'T4': 3,
  'Tier 4': 3
};

const getTouchCount = (tier) => {
  if (!tier) return 3;
  const count = TIER_TOUCH_COUNT[tier];
  console.log('🎯 getTouchCount:', { tier, count });
  return count || 3;
};

const SPANISH_COUNTRIES = new Set([
  'Venezuela', 'Chile', 'Argentina', 'Mexico', 'Costa Rica',
  'Uruguay', 'Colombia', 'Peru'
]);

function detectLanguageFromCountry(country) {
  if (!country) return 'English';
  if (country === 'Brazil') return 'Brazilian Portuguese';
  if (country === 'France') return 'French';
  if (SPANISH_COUNTRIES.has(country)) return 'Spanish';
  return 'English';
}

const MESSAGE_LANGUAGE_OPTIONS = [
  { value: 'English', label: '🇬🇧 English' },
  { value: 'Spanish', label: '🇪🇸 Spanish' },
  { value: 'Brazilian Portuguese', label: '🇧🇷 Portuguese' },
  { value: 'French', label: '🇫🇷 French' }
];

export default function PlaybookPreview({ currentTenant: currentTenantProp }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const user = contextUser;
  const [account, setAccount] = useState(null);
  const [playbook, setPlaybook] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [blockingIssues, setBlockingIssues] = useState([]);
  const [skippedContacts, setSkippedContacts] = useState([]);
  const [playbookChannels, setPlaybookChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProductContexts, setSelectedProductContexts] = useState([]);
  const [messageLanguage, setMessageLanguage] = useState('English');
  const [languageAutoDetected, setLanguageAutoDetected] = useState(false);
  const [blockedRetryItems, setBlockedRetryItems] = useState([]);
  const [blockedRetryErrors, setBlockedRetryErrors] = useState({});
  const [retryingStepIds, setRetryingStepIds] = useState([]);
  const [blockedRetryCount, setBlockedRetryCount] = useState(0);
  const [enrolledPlaybooksByPersonaId, setEnrolledPlaybooksByPersonaId] = useState({});
  const blockedToastFiredRef = useRef(false);

  useEffect(() => {
    loadPreview();
  }, [currentTenant, contextUser]);

  useEffect(() => {
    const loadEnrollmentBadges = async () => {
      if (!contextUser?.id || !preview?.preview?.length || !playbook?.key) {
        setEnrolledPlaybooksByPersonaId({});
        return;
      }

      const playbookKey = String(playbook.key || '').toLowerCase();
      const isStandardPlaybook =
        !playbookKey.startsWith('event::') &&
        !playbookKey.startsWith('diy_') &&
        !playbookKey.includes('custom');

      if (!isStandardPlaybook) {
        setEnrolledPlaybooksByPersonaId({});
        return;
      }

      const tenantId = resolveEffectiveTenant(
        contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
        currentTenant,
        contextUser,
        'instructure'
      );

      const uniquePersonaIds = Array.from(
        new Set(
          preview.preview
            .map((item) => item?.contact?.persona_id)
            .filter(Boolean)
            .map((id) => String(id))
        )
      );

      if (uniquePersonaIds.length === 0) {
        setEnrolledPlaybooksByPersonaId({});
        return;
      }

      try {
        const responses = await Promise.all(
          uniquePersonaIds.map(async (personaId) => {
            try {
              const r = await fetch(
                `${API_BASE_URL}/api/personas/${encodeURIComponent(personaId)}/playbook-status?tenant_id=${tenantId}&user_id=${encodeURIComponent(contextUser.id)}`,
                { credentials: 'include' }
              );
              if (!r.ok) return [personaId, { names: [], isEnrolled: false }];
              const data = await r.json();
              const names = [];
              if (data?.playbook?.playbook_name) {
                names.push(String(data.playbook.playbook_name));
              }
              if (Array.isArray(data?.active_playbooks)) {
                data.active_playbooks.forEach((p) => {
                  const name = p?.playbook_name || p?.name;
                  if (name) names.push(String(name));
                });
              }
              return [
                personaId,
                {
                  names: Array.from(new Set(names)),
                  isEnrolled: Boolean(data?.is_enrolled || (Array.isArray(data?.active_playbooks) && data.active_playbooks.length > 0))
                }
              ];
            } catch {
              return [personaId, { names: [], isEnrolled: false }];
            }
          })
        );

        const next = {};
        responses.forEach(([personaId, info]) => {
          const names = Array.isArray(info?.names) ? info.names : [];
          const isEnrolled = Boolean(info?.isEnrolled);
          if (isEnrolled || names.length > 0) {
            next[personaId] = { names, isEnrolled };
          }
        });
        setEnrolledPlaybooksByPersonaId(next);
      } catch {
        setEnrolledPlaybooksByPersonaId({});
      }
    };

    loadEnrollmentBadges();
  }, [preview, playbook, contextUser, currentTenant]);

  const formatBlockedStepLabel = (step) => {
    const name = step?.persona_name || 'Unknown persona';
    const touch = step?.step_number ? `Touch ${step.step_number}` : 'Touch ?';
    const channel = step?.channel || 'unknown channel';
    return `${name} (${touch}, ${channel})`;
  };

  const retryStep = async (stepId) => {
    if (!stepId || !user?.id) return;
    const adminTenant = user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    const tenantId = resolveEffectiveTenant(adminTenant, currentTenant, user, 'instructure');

    setRetryingStepIds((prev) => (prev.includes(stepId) ? prev : [...prev, stepId]));
    setBlockedRetryErrors((prev) => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
    try {
      const r = await fetch(`${API_BASE_URL}/api/playbooks/regenerate-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step_id: stepId, tenant_id: tenantId, user_id: user.id }),
      });
      const data = await r.json().catch(() => ({}));
      if (data?.success) {
        setBlockedRetryItems((prev) => prev.filter((item) => item?.step_id !== stepId));
        setBlockedRetryErrors((prev) => {
          const next = { ...prev };
          delete next[stepId];
          return next;
        });
      } else {
        setBlockedRetryErrors((prev) => ({
          ...prev,
          [stepId]: data?.qa_issues?.[0] || data?.reason || 'Still failing',
        }));
      }
    } catch (error) {
      setBlockedRetryErrors((prev) => ({
        ...prev,
        [stepId]: error?.message || 'Still failing',
      }));
    } finally {
      setRetryingStepIds((prev) => prev.filter((id) => id !== stepId));
    }
  };

  useEffect(() => {
    if (!blockedRetryItems.length) return;
    toast({
      title: `⚠️  ${blockedRetryCount || blockedRetryItems.length} message(s) couldn't be generated. Retry below.`,
      variant: 'destructive',
      duration: Infinity,
      description: (
        <div className="space-y-2 mt-2">
          {blockedRetryItems.map((step) => {
            const stepId = step?.step_id;
            const retrying = retryingStepIds.includes(stepId);
            return (
              <div key={stepId || `${step?.person_id || 'unknown'}-${step?.step_number || 'na'}`} className="border border-red-200 rounded p-2 bg-red-50/60">
                <div className="text-xs">{formatBlockedStepLabel(step)}</div>
                {blockedRetryErrors[stepId] && (
                  <div className="text-[11px] text-red-700 mt-1">{blockedRetryErrors[stepId]}</div>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  className="mt-2 h-7 text-xs"
                  onClick={() => retryStep(stepId)}
                  disabled={!stepId || retrying}
                >
                  {retrying ? 'Retrying...' : 'Retry'}
                </Button>
              </div>
            );
          })}
        </div>
      ),
    });
  }, [blockedRetryItems, blockedRetryErrors, retryingStepIds, blockedRetryCount, toast]);

  const loadPreview = async () => {
    if (!contextUser) return;
    setIsLoading(true);
    setError(null);
    setSkippedContacts([]);
    setPlaybookChannels([]);
    
    try {
      if (!contextUser.id) {
        setError('Unable to load user information. Please try logging in again.');
        setIsLoading(false);
        return;
      }

      // Profile from shared context (Layout bootstrap) - no direct fetch
      // Get parameters from URL
      const searchParams = new URLSearchParams(window.location.search);
      const accountId = searchParams.get('account_id');
      const playbookKeyRaw = searchParams.get('playbook_key');
      const playbookKey = normalizePlaybookKey(playbookKeyRaw);
      const mode = searchParams.get('mode') || 'untouched';
      const debugId = searchParams.get('debug_id') || `pb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      console.log('[PLAYBOOK_PREVIEW_LOAD]', {
        debugId,
        accountId,
        playbookKey,
        mode,
        currentTenant,
        url: window.location.href
      });

      if (!accountId || !playbookKey) {
        setError('Missing required parameters. Please select a playbook from the Playbooks page.');
        setIsLoading(false);
        return;
      }

      // Load account
      const accounts = await AccountFinal.filter({ account_id: accountId });
      if (accounts.length === 0) {
        setError('Account not found');
        setIsLoading(false);
        return;
      }
      const loadedAccount = accounts[0];
      setAccount(loadedAccount);
      const detectedLanguage = detectLanguageFromCountry(loadedAccount.country);
      setMessageLanguage(prev => {
        // Only auto-set if user hasn't manually changed it yet
        if (prev === 'English' && !languageAutoDetected) {
          return detectedLanguage;
        }
        return prev;
      });
      setLanguageAutoDetected(true);
      console.log('🌐 Account country for language detection:', loadedAccount.country, '→', detectedLanguage);

      const tenantId = resolveEffectiveTenant(
        contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
        currentTenant,
        contextUser,
        'instructure'
      );
      console.log('🔍 Resolved tenant ID:', tenantId);

      const normalizedCountry = String(
        loadedAccount?.country || loadedAccount?.country_name || ''
      ).trim().toLowerCase();

      const isBlockedEvasysGovernancePreview =
        isEvasysFamilyTenant(tenantId) &&
        playbookKey === 'evasys_governance_accreditation_ie_v2' &&
        (
          normalizedCountry === 'united states' ||
          normalizedCountry === 'usa' ||
          normalizedCountry === 'us' ||
          normalizedCountry === 'canada'
        );

      if (isBlockedEvasysGovernancePreview) {
        setError('This account is not eligible for the Governance playbook.');
        setIsLoading(false);
        return;
      }

      if (
        isEvasysFamilyTenant(tenantId) &&
        playbookKey === 'evaexam_integrity_assessment_security_v2' &&
        !isEvasysAccountEligibleForExamIntegrityPlaybook(loadedAccount.target_products)
      ) {
        setError(
          'This account is not eligible for the Academic Integrity playbook (target products must be EXAM or ALL).'
        );
        setIsLoading(false);
        return;
      }

      if (
        isEvasysFamilyTenant(tenantId) &&
        playbookKey === 'evasys_governance_accreditation_ie_v2' &&
        !isEvasysAccountEligibleForSysGovernancePlaybook(loadedAccount.target_products)
      ) {
        setError(
          'This account is not eligible for the Governance playbook (target products must be SYS or ALL).'
        );
        setIsLoading(false);
        return;
      }

      // Product context / selectedProductContexts initialization
      // LearnWise: no product selector; backend infers from selected personas
      if (tenantId === 'learnwise') {
        setSelectedProductContexts([]);
      } else if (isSysExamProductContextTenant(tenantId)) {
        if (isEvasysFamilyTenant(tenantId) && playbookKey === 'evaexam_integrity_assessment_security_v2') {
          setSelectedProductContexts(['EXAM']);
        } else if (isEvasysFamilyTenant(tenantId) && playbookKey === 'evasys_governance_accreditation_ie_v2') {
          setSelectedProductContexts(['SYS']);
        } else {
          setSelectedProductContexts(
            getEvasysDefaultSelectedProductContexts(
              loadedAccount.target_products,
              loadedAccount.country,
              loadedAccount.country_name
            )
          );
        }
      } else if (tenantId === 'cayuse') {
        setSelectedProductContexts(
          getCayuseDefaultSelectedProductContexts(loadedAccount.target_products)
        );
      } else {
        setSelectedProductContexts([]);
      }

      // Use Supabase account_id instead of Base44's UUID
      const backendAccountId = loadedAccount.account_id || accountId;

      // Evasys / Learnwise: gate by eligible-accounts when available
      // Learnwise must NOT hard-fail here because the Playbooks page already supports fallback
      if (isEvasysFamilyTenant(tenantId) || tenantId === 'learnwise') {
        console.log('[ELIGIBLE_ACCOUNTS_REQUEST]', {
          debugId,
          tenantId,
          playbookKey,
          backendAccountId,
          accountId
        });

        const eaOpts = {
          credentials: 'include',
          headers: { 'X-Tenant-ID': tenantId }
        };

        // Gate checks account scope only; keep this broad (`mode=all`).
        // Per-persona strictness remains in eligible-contacts below via URL `mode`.
        const eaRes = await fetch(
          `${API_BASE_URL}/api/playbooks/eligible-accounts?tenant_id=${tenantId}&playbook_key=${encodeURIComponent(playbookKey)}&debug_id=${encodeURIComponent(debugId)}&user_id=${encodeURIComponent(contextUser.id)}&mode=all`,
          eaOpts
        );

        const eaData = eaRes.ok ? await eaRes.json() : null;

        console.log('[ELIGIBLE_ACCOUNTS_RESPONSE]', {
          debugId,
          ok: eaRes.ok,
          status: eaRes.status,
          payload: eaData
        });

        if (eaRes.ok) {
          const eligibleAccounts = eaData.accounts || [];
          const eligibleIds = new Set(
            eligibleAccounts.flatMap(a => [a.account_id, a.id].filter(Boolean))
          );

          const isEligible =
            eligibleIds.has(backendAccountId) || eligibleIds.has(accountId);

          if (!isEligible) {
            setError(
              eaData?.excluded_reason ||
              (mode === 'enrolled'
                ? 'No enrolled personas available for this playbook'
                : 'Not eligible for this playbook')
            );
            setIsLoading(false);
            return;
          }
        } else if (isEvasysFamilyTenant(tenantId)) {
          // Keep evasys strict if that endpoint is expected to exist
          setError('Unable to load eligible accounts. Please try again.');
          setIsLoading(false);
          return;
        } else {
          // Learnwise fallback: do not block preview if eligible-accounts endpoint is missing
          console.warn('[ELIGIBLE_ACCOUNTS_BYPASS_LEARNWISE]', {
            debugId,
            tenantId,
            status: eaRes.status,
            reason: 'eligible-accounts endpoint unavailable for learnwise; continuing to eligible-contacts'
          });
        }
      }

      // Check blocking issues
      const issues = await checkBlockingIssues(tenantId, contextUser);
      setBlockingIssues(issues);

      console.log('🔍 Account details:', {
        base44_id: accountId,
        supabase_account_id: backendAccountId,
        account_name: loadedAccount.account_name,
        tenant_id: tenantId
      });

      // ============================================================================
      // USE BACKEND API FOR CONTACT FILTERING
      // ============================================================================
      console.log('[ELIGIBLE_CONTACTS_REQUEST]', {
        debugId,
        tenantId,
        playbookKey,
        backendAccountId
      });

      const fetchOpts = { credentials: 'include' };
      if (isEvasysFamilyTenant(tenantId) || tenantId === 'learnwise') fetchOpts.headers = { 'X-Tenant-ID': tenantId };
      const eligibleContactsResponse = await fetch(
        `${API_BASE_URL}/api/playbooks/${playbookKey}/eligible-contacts?tenant_id=${tenantId}&account_id=${encodeURIComponent(backendAccountId)}&debug_id=${encodeURIComponent(debugId)}&user_id=${encodeURIComponent(contextUser.id)}&mode=${encodeURIComponent(mode)}`,
        fetchOpts
      );

      const eligibleData = eligibleContactsResponse.ok ? await eligibleContactsResponse.json() : null;
      console.log('[ELIGIBLE_CONTACTS_RESPONSE]', {
        debugId,
        ok: eligibleContactsResponse.ok,
        status: eligibleContactsResponse.status,
        payload: eligibleData
      });

      if (!eligibleContactsResponse.ok) {
        const raw = await eligibleContactsResponse.text().catch(() => '');
        console.error('❌ eligible-contacts failed:', {
          status: eligibleContactsResponse.status,
          body: raw,
          tenantId,
          playbookKey,
          backendAccountId
        });

        const isChannelEligibilityPreviewFailure =
          eligibleContactsResponse.status === 500 &&
          raw.includes(CHANNEL_ELIGIBILITY_PREVIEW_ERROR_SNIPPET);
        setError(
          isChannelEligibilityPreviewFailure
            ? 'This account reached preview, but the backend could not verify channel-ready personas for this playbook. Please retry after the backend channel-eligibility fix is deployed.'
            : 'Unable to load eligible contacts for this playbook.'
        );
        setIsLoading(false);
        return;
      }
      const allContacts = (eligibleData.contacts || []).filter((c) => c?.is_visible !== false);
      const eligibleContacts = allContacts.filter((c) => c.execution_eligible !== false);
      const eligibilityMeta = eligibleData.execution_eligibility || null;
      const skippedFromMeta = Array.isArray(eligibilityMeta?.skipped_contacts) ? eligibilityMeta.skipped_contacts : [];
      const skippedFromContacts = allContacts
        .filter((c) => c.execution_eligible === false)
        .map((c) => ({
          personId: c.person_id || c.id || c.persona_id,
          firstName: c.first_name,
          lastName: c.last_name,
          archetype: c.archetype || c.archetype_name,
          personaChannels: Array.isArray(c.persona_channels) ? c.persona_channels : [],
          playbookChannels: Array.isArray(eligibilityMeta?.playbook_channels) ? eligibilityMeta.playbook_channels : [],
          channelReasons: c.channel_reasons || {},
          reason: c.reason || 'No supported channels for this run',
        }));
      const mergedSkipped = skippedFromMeta.length > 0 ? skippedFromMeta : skippedFromContacts;
      setSkippedContacts(mergedSkipped);
      setPlaybookChannels(
        Array.isArray(eligibilityMeta?.playbook_channels)
          ? eligibilityMeta.playbook_channels
          : []
      );

      console.log('✅ Backend returned eligible contacts:', {
        selectedCount: allContacts.length,
        eligibleCount: eligibleContacts.length,
        skippedCount: mergedSkipped.length,
        requirements: eligibleData.requirements,
        topContacts: eligibleContacts.slice(0, 3).map(c => ({
          name: `${c.first_name} ${c.last_name}`,
          archetype: c.archetype_name,
          fit: c.persona_fit_score,
          score: c.weighted_score
        }))
      });

      if (eligibleContacts.length === 0) {
        const contactsLen = allContacts.length;
        const metaSkipped = Number(eligibilityMeta?.skipped_count);
        const skippedCount = Math.max(
          mergedSkipped.length,
          Number.isFinite(metaSkipped) && metaSkipped > 0 ? metaSkipped : 0
        );
        const backendRemaining = Number(eligibleData?.eligible_remaining);
        const backendShowsRemainingButNoContacts =
          contactsLen === 0 && Number.isFinite(backendRemaining) && backendRemaining > 0;

        const noExecutionEligibleChannelSafeguard =
          contactsLen === 0 && (skippedCount > 0 || backendShowsRemainingButNoContacts);

        if (noExecutionEligibleChannelSafeguard) {
          setPreviewNoExecutionEligible(tenantId, playbookKey, backendAccountId);
          setError(
            'This account has no execution-eligible personas for this playbook. Remaining targeted personas were excluded by channel requirements.'
          );
        } else {
          setError(
            eligibleData.excluded_reason ||
            (mergedSkipped.length > 0
              ? 'All targeted personas were skipped by channel-support eligibility for this run.'
              : 'No remaining eligible personas for this playbook')
          );
        }
        setIsLoading(false);
        return;
      }

      clearPreviewNoExecutionEligible(tenantId, playbookKey, backendAccountId);

      // Build preview data from backend response
      const mockPreview = buildPreviewFromContacts(eligibleContacts, loadedAccount);
      setPreview(mockPreview);

      // Also fetch playbook details for display
      const playbookFetchOpts = { credentials: 'include' };
      if (isEvasysFamilyTenant(tenantId) || tenantId === 'learnwise') playbookFetchOpts.headers = { 'X-Tenant-ID': tenantId };
      const playbookResponse = await fetch(
        `${API_BASE_URL}/api/playbooks/${playbookKey}?tenant_id=${tenantId}`,
        playbookFetchOpts
      );

      let playbookName = playbookKey.replace(/_/g, ' ').replace(/^es_/, '').toUpperCase();
      if (playbookResponse.ok) {
        const playbookData = await playbookResponse.json();
        playbookName = playbookData.name || playbookName;
      }

      setPlaybook({ 
        key: playbookKey, 
        name: playbookName
      });

      setSelectedContacts(new Set());

    } catch (error) {
      console.error('Error loading preview:', error);
      setError(error.message || 'Failed to load playbook preview');
    } finally {
      setIsLoading(false);
    }
  };

  // Build preview from backend eligible-contacts response
  const buildPreviewFromContacts = (contacts, loadedAccount) => {
    const accountTier = loadedAccount.account_tier || '3';
    const touchCount = getTouchCount(accountTier);

    console.log('🔄 Building preview from backend contacts:', {
      contactsCount: contacts.length,
      accountTier,
      touchCount
    });

    const generateTouches = (numTouches) => {
      const touches = [];
      const channels = ['email', 'linkedin', 'call', 'email', 'linkedin', 'call'];
      
      for (let i = 0; i < numTouches; i++) {
        touches.push({
          touch_number: i + 1,
          channel: channels[i % channels.length],
          delay_days: i * 3,
          day_offset: i * 3,
          subject: null,
          preview: null
        });
      }
      
      return touches;
    };

    const preview = contacts.map(person => {
      const touches = generateTouches(touchCount);
      
      return {
        contact: {
          persona_id: person.person_id || person.id,
          name: `${person.first_name} ${person.last_name}`,
          title: person.role_title || person.archetype_name || '',
          archetype: person.archetype_name || '',
          email: person.email,
          linkedin_url: person.linkedin_url,
          phone: person.phone,
          persona_fit_score: person.persona_fit_score,
          worked_for_customer: person.worked_for_customer,
          weighted_score: person.weighted_score,  // Include backend score
          product_affinity: person.product_affinity || null
        },
        tier: accountTier,
        expected_touches: touchCount,
        sequence: touches,
        touches: touches
      };
    });

    return {
      playbook_name: '',
      account_name: loadedAccount.account_name,
      tier: accountTier,
      preview: preview,
      is_mock: false
    };
  };

  // Transform API response to match UI expectations (kept for future API integration)
  const transformApiResponse = (apiData, loadedAccount) => {
    const contacts = apiData.contacts || [];
    const accountTier = apiData.account?.tier || loadedAccount.account_tier || '3';
    const touchCount = getTouchCount(accountTier);

    console.log('🔄 Transforming API response:', {
      contactsCount: contacts.length,
      accountTier,
      touchCount
    });

    // Generate touches for each contact
    const generateTouches = (numTouches) => {
      const touches = [];
      const channels = ['email', 'linkedin', 'call', 'email', 'linkedin', 'call'];
      
      for (let i = 0; i < numTouches; i++) {
        touches.push({
          touch_number: i + 1,
          channel: channels[i % channels.length],
          delay_days: i * 3,
          day_offset: i * 3,
          subject: null,
          preview: null
        });
      }
      
      return touches;
    };

    const preview = contacts.map(contact => {
      const touches = generateTouches(touchCount);
      
      return {
        contact: {
          persona_id: contact.person_id,
          name: `${contact.first_name} ${contact.last_name}`,
          title: contact.role_title || '',
          archetype: contact.archetype || '',
          email: contact.email,
          linkedin_url: contact.linkedin_url,  // Keep original field name
          phone: contact.phone,
          persona_fit_score: contact.persona_fit_score,
          worked_for_customer: contact.worked_for_customer,
          product_affinity: contact.product_affinity || null
        },
        tier: accountTier,
        expected_touches: touchCount,
        sequence: touches,
        touches: touches
      };
    });

    return {
      playbook_name: apiData.playbook?.name || '',
      account_name: apiData.account?.name || loadedAccount.account_name,
      tier: accountTier,
      preview: preview,
      is_mock: false
    };
  };

  const checkBlockingIssues = async (tenantId, user) => {
    const issues = [];
    let hasEmailProvider = false;

    if (user?.id) {
      try {
        const [gmailResponse, outlookResponse] = await Promise.all([
          fetch(
            `${API_BASE_URL}/api/gmail/status?tenant_id=${tenantId}&user_id=${user.id}`,
            { credentials: 'include' },
          ),
          fetch(
            `${API_BASE_URL}/api/outlook/status?tenant_id=${tenantId}&user_id=${user.id}`,
            { credentials: 'include' },
          ),
        ]);

        if (gmailResponse.ok) {
          const gmailData = await gmailResponse.json();
          hasEmailProvider = hasEmailProvider || gmailData.connected === true;
        }
        if (outlookResponse.ok) {
          const outlookData = await outlookResponse.json();
          hasEmailProvider = hasEmailProvider || outlookData.connected === true;
        }
      } catch (e) {
        console.warn('Could not check email provider status:', e);
      }
    }

    if (!hasEmailProvider) {
      issues.push({
        type: 'email_provider',
        title: 'Email not connected',
        description:
          'Choose Gmail or Outlook in Settings → Emails before creating playbook drafts.',
        action: () => navigate(`${createPageUrl('Settings')}?tab=gmail`),
      });
    }

    return issues;
  };

  const handleToggleContact = (personaId) => {
    setSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(personaId)) {
        next.delete(personaId);
      } else {
        next.add(personaId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (preview && preview.preview) {
      if (selectedContacts.size === preview.preview.length) {
        setSelectedContacts(new Set());
      } else {
        const allIds = preview.preview.map(p => p.contact.persona_id);
        setSelectedContacts(new Set(allIds));
      }
    }
  };

  const handleCreateDrafts = async () => {
    const debugId = new URLSearchParams(window.location.search).get('debug_id') || `pb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    console.log('[CREATE_DRAFTS_CLICK]', {
      debugId,
      selectedContacts: Array.from(selectedContacts),
      selectedCount: selectedContacts.size,
      previewContacts: preview?.preview?.map(p => ({
        persona_id: p.contact.persona_id,
        name: p.contact.name
      }))
    });

    if (blockingIssues.length > 0) {
      toast({
        title: 'Cannot create drafts',
        description: 'Please resolve all blocking issues first',
        variant: 'destructive'
      });
      return;
    }

    if (selectedContacts.size === 0) {
      toast({
        title: 'No contacts selected',
        description: 'Please select at least one contact',
        variant: 'destructive'
      });
      return;
    }

    setIsCreating(true);

    try {
      const adminTenant =
        user?.role === 'admin'
          ? localStorage.getItem('admin-active-tenant')
          : null;
      const tenantId = resolveEffectiveTenant(
        adminTenant,
        currentTenant,
        user,
        'instructure'
      );
      console.log('🔍 Draft creation tenant ID:', tenantId);

      // Get full contact details from preview
      const contactsData = Array.from(selectedContacts).map(personaId => {
        const previewItem = preview.preview.find(p => p.contact.persona_id === personaId);
        const contact = previewItem?.contact;
        
        return {
          persona_id: personaId, // Primary reference to personas table (source of truth)
          person_id: personaId, // Keep for backward compatibility
          firstName: contact?.name?.split(' ')[0],
          lastName: contact?.name?.split(' ').slice(1).join(' '),
          // Contact data should come from personas table at creation time
          // Backend should join with personas table to get current values
          email: contact?.email,
          linkedin_url: contact?.linkedin_url,
          phone: contact?.phone,
          archetype: contact?.archetype,
          product_affinity: contact?.product_affinity || null
        };
      });

      const senderName = user?.display_name || user?.full_name || 'Sales Team';
      const senderTitle = user?.job_title || 'Account Executive';
      const senderCompany = user?.company_name || 'our team';

      console.log('[CREATE_REQUEST_PAYLOAD]', {
        debugId,
        tenantId,
        accountId: account.account_id || account.id,
        playbookKey: playbook.key,
        contactsData
      });

      // Step 1: Start the job (returns immediately)
      const createHeaders = {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId,
        'x-user-id': user.id
      };
      const createPayload = {
        tenant_id: tenantId,
        account_id: account.account_id || account.id,
        playbook_key: playbook.key,
        contacts: contactsData,
        user_id: user.id,
        sender_name: senderName,
        sender_title: senderTitle,
        sender_company: senderCompany,
        debug_id: debugId,
        language: messageLanguage
      };

      if (tenantId !== 'learnwise') {
        const createPk = normalizePlaybookKey(playbook.key);
        if (isEvasysFamilyTenant(tenantId) && createPk === 'evaexam_integrity_assessment_security_v2') {
          createPayload.product_contexts = ['EXAM'];
        } else if (isEvasysFamilyTenant(tenantId) && createPk === 'evasys_governance_accreditation_ie_v2') {
          createPayload.product_contexts = ['SYS'];
        } else if (isSysExamProductContextTenant(tenantId)) {
          let ctx = Array.isArray(selectedProductContexts) ? [...selectedProductContexts] : [];
          if (
            ctx.length === 0 &&
            isEvasysNorthAmericaCountry(account.country, account.country_name) &&
            getEvasysTargetProductListForContext(account.target_products).length > 1
          ) {
            ctx = ['EXAM'];
          }
          if (ctx.length > 0) {
            createPayload.product_contexts = ctx;
          }
        } else if (tenantId === 'cayuse') {
          const ctx = Array.isArray(selectedProductContexts) ? [...selectedProductContexts] : [];
          if (ctx.length > 0) {
            createPayload.product_contexts = ctx;
          }
        } else if (selectedProductContexts?.length > 0) {
          createPayload.product_contexts = selectedProductContexts;
        }
      }

      const createResponse = await fetch(`${API_BASE_URL}/api/playbooks/create`, {
        method: 'POST',
        headers: createHeaders,
        credentials: 'include',
        body: JSON.stringify(createPayload),
        signal: typeof AbortSignal?.timeout === 'function' ? AbortSignal.timeout(CREATE_REQUEST_TIMEOUT_MS) : undefined
      });

      if (!createResponse.ok) {
        const raw = await createResponse.text();
        let createErrorBody = null;
        try {
          createErrorBody = raw ? JSON.parse(raw) : null;
        } catch {
          createErrorBody = null;
        }
        if (createErrorBody?.code === 'NO_ELIGIBLE_CONTACTS_FOR_CHANNELS') {
          throw new Error(
            createErrorBody?.error ||
            'All selected personas were skipped by channel-support eligibility for this run.'
          );
        }
        console.error('❌ Create sequence failed:', raw);
        throw new Error(`Backend API error: ${createResponse.status} - ${raw}`);
      }

      const createResult = await createResponse.json();
      const { steps_created } = createResult;
      const sequenceIds = normalizeRunSequenceIds(createResult);
      const expectedSecondsRun = Number(createResult?.expected_seconds) || 300;
      if (Array.isArray(createResult?.skipped_contacts) && createResult.skipped_contacts.length > 0) {
        const skippedCount = createResult.skipped_contacts.length;
        toast({
          title: 'Some personas were skipped',
          description: `${skippedCount} persona${skippedCount === 1 ? '' : 's'} did not support this run's channels and were excluded from execution.`,
        });
      }
      console.log('✅ Sequence started:', sequenceIds, 'Steps:', steps_created);

      // If steps were created synchronously, redirect immediately
      if (steps_created && steps_created > 0 && sequenceIds.length === 0) {
        console.log('✅ Steps created synchronously, redirecting to Outbox...');
        toast({
          title: 'Drafts created!',
          description: `Created ${steps_created} drafts`,
        });
        setIsCreating(false);
        setTimeout(
          () => navigate(createPageUrl('Outbox'), { state: OUTBOX_POST_GENERATION_NAV_STATE }),
          1000
        );
        return;
      }

      // If no sequence ids returned, assume sync completion
      if (sequenceIds.length === 0) {
        console.log('✅ No sequence_id - assuming sync completion, redirecting...');
        toast({
          title: 'Drafts created!',
          description: `Created drafts for ${selectedContacts.size} contacts`,
        });
        setIsCreating(false);
        setTimeout(
          () => navigate(createPageUrl('Outbox'), { state: OUTBOX_POST_GENERATION_NAV_STATE }),
          1000
        );
        return;
      }

      // Step 2: Poll for completion (one or more sequence_ids)
      let pollCount = 0;
      let isComplete = false;
      const peopleCount = selectedContacts.size;
      const inferredStepCount = Number(
        playbook?.steps_count ?? playbook?.step_count ?? playbook?.steps?.length ?? getTouchCount(preview?.account_tier || account?.account_tier)
      ) || 1;
      const explicitMessageCount = Number(createResult?.total_steps ?? createResult?.steps_created);
      const estimatedMessages = Number.isFinite(explicitMessageCount) && explicitMessageCount > 0
        ? explicitMessageCount
        : peopleCount * inferredStepCount;
      const watchWindowMs = computeWatchWindowMs(estimatedMessages);
      const watchStartedAt = Date.now();
      const statusLastBodyById = {};

      const pollStatus = async () => {
        if (isComplete) return true;

        try {
          pollCount += 1;
          const statusBodies = await fetchPlaybookStatusBodiesAllSettled(
            API_BASE_URL,
            sequenceIds,
            tenantId,
            user?.id,
            statusLastBodyById
          );

          const parsedList = statusBodies.map(parsePlaybookStatusData);
          const phases = parsedList.map(sequencePollPhase);
          const agg = aggregateParsedList(parsedList);
          const blockedSteps = statusBodies.flatMap((b) =>
            Array.isArray(b?.blocked_steps) ? b.blocked_steps : []
          );
          const blockedCount = agg.blocked;

          console.log(
            `📊 Poll ${pollCount}: sequences=${sequenceIds.length}, complete=${agg.complete}/${agg.total}, phases=${phases.join(',')}`
          );

          const failedSeq = phases.filter((p) => p === 'failed').length;
          if (failedSeq > 0) {
            isComplete = true;
            toast({
              title: 'Generation failed',
              description: describeSequencesFailed(sequenceIds.length, failedSeq, agg.complete),
              variant: 'destructive',
            });
            setIsCreating(false);
            return true;
          }

          const noStepsSeq = phases.filter((p) => p === 'no_steps').length;
          if (noStepsSeq > 0) {
            isComplete = true;
            toast({
              title: 'No steps to generate',
              description:
                sequenceIds.length > 1
                  ? `${noStepsSeq} sequence${noStepsSeq === 1 ? '' : 's'} had no steps.`
                  : 'Sequence has no steps yet.',
              variant: 'destructive',
            });
            setIsCreating(false);
            return true;
          }

          const allDone = phases.every((p) => p === 'done');
          if (allDone) {
            isComplete = true;
            if (blockedCount > 0 && !blockedToastFiredRef.current) {
              blockedToastFiredRef.current = true;
              setBlockedRetryCount(blockedCount);
              setBlockedRetryErrors({});
              setRetryingStepIds([]);
              setBlockedRetryItems(blockedSteps);
            } else {
              toast({
                title: 'Drafts created!',
                description: `Created ${agg.complete || selectedContacts.size} drafts`,
              });
            }
            setIsCreating(false);

            if (blockedCount === 0) {
              console.log('🚀 Redirecting to Outbox...');
              setTimeout(
                () => navigate(createPageUrl('Outbox'), { state: OUTBOX_POST_GENERATION_NAV_STATE }),
                1000
              );
            }
            return true;
          }

          toast({
            title: 'Creating drafts...',
            description: formatGeneratingProgressLine(agg, expectedSecondsRun, estimatedMessages),
            duration: 4000,
          });
        } catch (pollError) {
          console.error('Polling error:', pollError);
        }

        return false;
      };

      await sleep(INITIAL_POLL_DELAY_MS);
      let done = await pollStatus();

      while (!done) {
        const elapsedMs = Date.now() - watchStartedAt;
        if (elapsedMs >= watchWindowMs) {
          isComplete = true;
          toast({
            title: 'Still running in the background',
            description: 'Generation is still running in the background. Large runs can take longer. You can safely leave this page.',
          });
          setIsCreating(false);
          setTimeout(
            () => navigate(createPageUrl('Outbox'), { state: OUTBOX_POST_GENERATION_NAV_STATE }),
            1500
          );
          break;
        }
        await sleep(getPollIntervalMs(elapsedMs));
        done = await pollStatus();
      }

    } catch (error) {
      console.error('❌ Error creating drafts:', error);
      
      toast({
        title: 'Failed to create drafts',
        description: error.message || 'Backend API is not available.',
        variant: 'destructive'
      });
      
      setIsCreating(false);
    }
  };

  // Loading state
  if (!contextUser) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
        <p className="text-[#5C6B7A]">Loading playbook preview...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <AlertDescription>
            <p className="font-semibold text-red-900">{error}</p>
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate(createPageUrl('Playbooks'))}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Playbooks
        </Button>
      </div>
    );
  }

  // No preview data
  if (!preview || !account || !playbook) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <AlertDescription>
            <p className="font-semibold text-yellow-900">No preview data available</p>
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate(createPageUrl('Playbooks'))}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Playbooks
        </Button>
      </div>
    );
  }

  // Main content
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(createPageUrl('Playbooks'))}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Playbooks
          </Button>
          <h1 className="text-3xl font-bold text-[#0A1A2F]">{playbook.name}</h1>
          <p className="text-lg text-[#5C6B7A] mt-1">{account.account_name}</p>
          <div className="flex items-center gap-2 mt-2">
            {(preview.account_tier || account.account_tier) && (
              <Badge variant="outline">
                {preview.account_tier || account.account_tier}
              </Badge>
            )}
            {preview.is_mock && (
              <Badge variant="secondary">Preview Mode</Badge>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-[#5C6B7A]">
            {selectedContacts.size} of {preview.preview.length} contacts selected
          </p>
        </div>
      </div>

      {/* Product context selector - only when account has multiple target products.
          Hidden for single-product playbooks: Academic Integrity (EXAM only), Governance (SYS only).
          For learnwise: hidden for Champion/Student Support; FEEDBACK+TUTOR for Teaching & Learning; SUPPORT+FEEDBACK+TUTOR for Executive/IT. */}
      {(() => {
        const singleProductPlaybooks = [
          'evaexam_integrity_assessment_security_v2',  // EXAM focused only
          'evasys_governance_accreditation_ie_v2'      // SYS focused only
        ];
        if (singleProductPlaybooks.includes(playbook?.key)) return null;

        const adminTenant = user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
        const tenantId = user ? resolveEffectiveTenant(adminTenant, currentTenant, user, 'instructure') : null;
        const playbookKey = playbook?.key || '';

        // LearnWise: no product selector; users personalize via persona selection (product_affinity badges)
        if (tenantId === 'learnwise') return null;

        // Evasys / Scantron: SYS/EXAM product context from account.target_products
        if (isSysExamProductContextTenant(tenantId)) {
          const products = getEvasysTargetProductListForContext(account.target_products);
          if (products.length <= 1) return null;
          const naDefaultExam = isEvasysNorthAmericaCountry(
            account.country,
            account.country_name
          );
          return (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Product context(s)</CardTitle>
            <p className="text-sm text-[#5C6B7A] mt-1">
              {naDefaultExam
                ? 'Select which products this playbook applies to. Default: EXAM for United States and Canada.'
                : 'Select which products this playbook applies to. Default: all.'}
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {products.map((product) => {
                const isSelected = selectedProductContexts.includes(product);
                return (
                  <label
                    key={product}
                    className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => {
                        setSelectedProductContexts(prev =>
                          checked
                            ? [...prev, product]
                            : prev.filter((p) => p !== product)
                        );
                      }}
                    />
                    <span className="text-sm font-medium">{product}</span>
                  </label>
                );
              })}
            </div>
          </CardContent>
        </Card>
          );
        }

        // Cayuse: GRAD / COMPLIANCE / SPONSOR from account.target_products
        if (tenantId === 'cayuse') {
          const products = getCayuseTargetProductListForContext(account.target_products);
          if (products.length <= 1) return null;
          return (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Product context(s)</CardTitle>
            <p className="text-sm text-[#5C6B7A] mt-1">
              Select which Cayuse product lanes this playbook applies to. Default: all lanes for this account.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {products.map((product) => {
                const isSelected = selectedProductContexts.includes(product);
                return (
                  <label
                    key={product}
                    className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => {
                        setSelectedProductContexts(prev =>
                          checked
                            ? [...prev, product]
                            : prev.filter((p) => p !== product)
                        );
                      }}
                    />
                    <span className="text-sm font-medium">{product}</span>
                  </label>
                );
              })}
            </div>
          </CardContent>
        </Card>
          );
        }

        return null;
      })()}

      {/* Language selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Message Language
            {languageAutoDetected && (
              <Badge variant="outline" className="text-xs font-normal">
                Auto-detected
              </Badge>
            )}
          </CardTitle>
          <p className="text-sm text-[#5C6B7A] mt-1">
            Messages will be generated in the selected language.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {MESSAGE_LANGUAGE_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => { setMessageLanguage(value); setLanguageAutoDetected(false); }}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  messageLanguage === value
                    ? 'bg-[#00C2C2] border-[#00C2C2] text-white'
                    : 'bg-white border-gray-200 text-[#5C6B7A] hover:border-[#00C2C2]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Blocking Issues */}
      {blockingIssues.length > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <AlertDescription>
            <p className="font-semibold text-yellow-900 mb-2">Action required before creating drafts:</p>
            <ul className="space-y-2">
              {blockingIssues.map((issue, idx) => (
                <li key={idx} className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-yellow-900">{issue.title}</p>
                    <p className="text-sm text-yellow-700">{issue.description}</p>
                  </div>
                  {issue.action && (
                    <Button size="sm" variant="outline" onClick={issue.action}>
                      Fix
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {skippedContacts.length > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <AlertDescription>
            <p className="font-semibold text-yellow-900">
              {skippedContacts.length} persona{skippedContacts.length !== 1 ? 's were' : ' was'} excluded
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              This playbook requires at least one supported contact method for:
              {playbookChannels.length > 0 ? ` ${playbookChannels.join(', ')}` : ' this run'}.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Contacts Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Select Contacts</CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedContacts.size === preview.preview.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {selectedContacts.size === 0 && (
            <p className="text-sm text-[#5C6B7A] mb-4">
              Select the personas you want to enroll in this playbook.
            </p>
          )}
          <div className="space-y-4">
            {preview.preview.map((item) => {
              const contact = item.contact;
              const isSelected = selectedContacts.has(contact.persona_id);
              const adminTenantForPreview = user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
              const previewTenantId = user ? resolveEffectiveTenant(adminTenantForPreview, currentTenant, user, 'instructure') : null;
              
              // Get touch count from item
              const touchCount = item.expected_touches || getTouchCount(item.tier);
              const touches = item.touches || item.sequence || [];
              
              return (
                <div
                  key={contact.persona_id}
                  className={`border rounded-lg p-4 transition-colors ${
                    isSelected ? 'border-[#00C2C2] bg-[#00C2C2]/5' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggleContact(contact.persona_id)}
                      className="mt-1"
                    />
                    
                    <div className="flex-1">
                      {/* Contact Info */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-[#0A1A2F]">{contact.name}</h3>
                          <p className="text-sm text-[#5C6B7A]">{contact.title}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {contact.archetype && (
                              <Badge variant="secondary" className="text-xs">
                                {contact.archetype}
                              </Badge>
                            )}
                            {contact.persona_fit_score != null && (
                              <Badge variant="outline" className="text-xs">
                                Fit: {contact.persona_fit_score}
                              </Badge>
                            )}
                            {contact.worked_for_customer && (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                Former Customer
                              </Badge>
                            )}
                            {(previewTenantId === 'learnwise' || previewTenantId === 'cayuse') &&
                              contact.product_affinity && (
                              <Badge variant="outline" className="text-xs">
                                {contact.product_affinity}
                              </Badge>
                            )}
                            {item.tier && (
                              <Badge variant="outline" className="text-xs">
                                {item.tier}
                              </Badge>
                            )}
                            {(() => {
                              const enrollmentInfo = enrolledPlaybooksByPersonaId[String(contact.persona_id)];
                              const enrolledNames = Array.isArray(enrollmentInfo?.names) ? enrollmentInfo.names : [];
                              const isEnrolled = Boolean(enrollmentInfo?.isEnrolled);
                              if (!isEnrolled && enrolledNames.length === 0) return null;
                              const title = enrolledNames.length > 0
                                ? `Already enrolled in: ${enrolledNames.join(', ')}`
                                : 'Already enrolled in another active playbook';
                              return (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-purple-50 text-purple-700 border-purple-200 cursor-help"
                                  title={title}
                                >
                                  Already Enrolled
                                </Badge>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Sequence Preview */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-[#5C6B7A]">
                          {touchCount} Touch Sequence:
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {touches.length > 0 ? (
                            touches.map((touch, touchIdx) => {
                              const ch = touch.channel;
                              const Icon = isLinkedInOutboxChannel({ channel: ch })
                                ? Linkedin
                                : CHANNEL_ICONS[ch] || Mail;
                              return (
                                <div
                                  key={touch.touch_number}
                                  className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md text-xs flex-wrap"
                                >
                                  {Icon && <Icon className="w-3 h-3" />}
                                  <span>Touch {touch.touch_number}</span>
                                  <LinkedInSubtypeBadge
                                    source={{
                                      channel: ch,
                                      allSteps: touches,
                                      stepIndex: touchIdx,
                                    }}
                                    debugContext="playbook-preview-touch"
                                  />
                                  {(touch.day_offset || touch.delay_days || 0) > 0 && (
                                    <span className="text-[#5C6B7A]">
                                      (+{touch.day_offset || touch.delay_days}d)
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <p className="text-xs text-gray-400">No touches configured</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Action Button */}
      <div className="flex justify-end gap-3">
        <Button 
          variant="outline" 
          onClick={() => navigate(createPageUrl('Playbooks'))}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateDrafts}
          disabled={selectedContacts.size === 0 || blockingIssues.length > 0 || isCreating}
          className="bg-[#00C2C2] hover:bg-[#00A3A3]"
        >
          {isCreating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating Drafts...
            </>
          ) : (
            <>
              <MessageSquare className="w-4 h-4 mr-2" />
              Create {selectedContacts.size} Draft{selectedContacts.size !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}